"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Lock, Building, Coffee, Truck, Phone, Mail, CheckCircle, ArrowRight, Shield, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { MotionDiv, MotionSection, fadeIn, fadeInLeft, fadeInRight, scaleIn } from "@/components/motion"
import Nav from "@/components/nav"
import Footer from "@/components/footer"

const wholesaleProducts = [
  { name: "Ethiopia Yirgacheffe", min: "5 lbs", price: "$14.50/lb", format: "Whole Bean / Ground" },
  { name: "Colombia Huila", min: "5 lbs", price: "$13.00/lb", format: "Whole Bean / Ground" },
  { name: "Northwind Espresso Blend", min: "10 lbs", price: "$12.00/lb", format: "Whole Bean / Ground" },
  { name: "Sunrise Blend", min: "10 lbs", price: "$11.50/lb", format: "Whole Bean / Ground" },
  { name: "Custom Blend", min: "25 lbs", price: "Contact us", format: "Whole Bean / Ground" },
  { name: "Cold Brew Concentrate", min: "1 gal", price: "$28.00/gal", format: "Ready-to-use" },
]

export default function WholesalePage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(true)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [applying, setApplying] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    // Demo login — in production, connect to Supabase auth
    if (email && password) {
      setIsLoggedIn(true)
    }
  }

  return (
    <>
      <Nav />

      {/* Hero */}
      <section className="relative pt-36 pb-24 bg-foreground overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-25 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="font-heading text-[10rem] font-bold text-white/[0.025] whitespace-nowrap">WHOLESALE</span>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <MotionDiv variants={fadeIn}>
            <Badge className="bg-primary text-primary-foreground border-0 mb-5 text-xs tracking-widest uppercase">
              B2B Portal
            </Badge>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-background mb-4">
              Wholesale &{" "}
              <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                Restaurant
              </span>{" "}
              Program
            </h1>
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-14 bg-accent" />
              <Building className="w-4 h-4 text-accent" />
              <div className="h-px w-14 bg-accent" />
            </div>
            <p className="text-background/60 max-w-xl mx-auto text-base md:text-lg">
              Premium specialty coffee for cafés, restaurants, hotels, and offices. Roasted-to-order, consistent supply, and Jamie's personal support.
            </p>
          </MotionDiv>
        </div>
      </section>

      {/* Wave */}
      <div className="relative bg-background">
        <svg className="absolute -top-1 left-0 w-full" viewBox="0 0 1440 55" fill="none" preserveAspectRatio="none">
          <path d="M0 55L1440 55L1440 0C1100 45 700 55 400 25C150 5 0 15 0 15Z" fill="hsl(20, 25%, 12%)" />
        </svg>
        <div className="h-12" />
      </div>

      {/* Benefits */}
      <section className="py-16 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/4 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <MotionSection className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
            {[
              { icon: <Truck className="w-6 h-6 text-primary" />, title: "Bi-weekly delivery", body: "Scheduled delivery routes across Portland metro. Flat-rate freight for larger orders." },
              { icon: <Coffee className="w-6 h-6 text-primary" />, title: "Custom roast profiles", body: "Existing wholesale clients can request roast adjustments. We'll dial it in with you." },
              { icon: <Star className="w-6 h-6 text-primary" />, title: "Barista training", body: "Free monthly training sessions at our roastery for wholesale client staff. Espresso calibration support." },
            ].map((item) => (
              <motion.div key={item.title} variants={scaleIn}>
                <div className="p-6 bg-card border border-border/60 rounded-2xl shadow-[var(--shadow-card)] h-full">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </motion.div>
            ))}
          </MotionSection>
        </div>
      </section>

      {/* Login gate OR portal */}
      <section className="py-12 bg-muted border-t border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {!isLoggedIn ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Login form */}
              <MotionDiv variants={fadeInLeft}>
                <div className="bg-card border border-border/60 rounded-2xl shadow-[var(--shadow-card-hover)] p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-bold text-foreground">Client Portal Login</h2>
                      <p className="text-xs text-muted-foreground">For approved wholesale accounts</p>
                    </div>
                  </div>

                  {showLoginForm ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="restaurant@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="mt-1"
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2 mt-2">
                        <Shield className="w-4 h-4" />
                        Sign In to Portal
                      </Button>
                      <p className="text-xs text-center text-muted-foreground pt-2">
                        Not a wholesale client yet?{" "}
                        <button
                          type="button"
                          onClick={() => setApplying(true)}
                          className="text-accent hover:underline font-medium"
                        >
                          Apply below
                        </button>
                      </p>
                    </form>
                  ) : null}
                </div>
              </MotionDiv>

              {/* Apply form */}
              <MotionDiv variants={fadeInRight}>
                <div className="bg-card border border-border/60 rounded-2xl shadow-[var(--shadow-card)] p-8">
                  <Badge className="bg-accent text-accent-foreground border-0 mb-4 text-xs tracking-widest uppercase">
                    New Partner Inquiry
                  </Badge>
                  <h2 className="font-heading text-xl font-bold text-foreground mb-2">Apply for a wholesale account</h2>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    We work with cafés, restaurants, hotels, and offices. Minimum order is 10 lbs/month. We'll respond within 2 business days.
                  </p>
                  <a
                    href="/documents/northwind-wholesale-price-sheet.pdf"
                    target="_blank"
                    rel="noopener"
                    className="mb-6 inline-block text-sm font-medium text-primary underline underline-offset-4 transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    Download our wholesale price sheet
                  </a>
                  <form
                    id="wholesale-inquiry-form"
                    data-hiveku-form-key="wholesale-inquiry"
                    data-hiveku-result="inline"
                    className="relative space-y-4"
                  >
                    <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
                      <input type="text" name="hvk_contact_pref" tabIndex={-1} autoComplete="off" />
                    </div>
                    <div>
                      <Label htmlFor="wholesale-contact-name" className="text-xs text-foreground">Your name</Label>
                      <Input
                        id="wholesale-contact-name"
                        name="full_name"
                        type="text"
                        autoComplete="name"
                        className="mt-1 text-sm"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="wholesale-business-name" className="text-xs text-foreground">Business name</Label>
                        <Input
                          id="wholesale-business-name"
                          name="business_name"
                          autoComplete="organization"
                          placeholder="The Good Cup"
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="wholesale-business-type" className="text-xs text-foreground">Business type</Label>
                        <Input
                          id="wholesale-business-type"
                          name="business_type"
                          autoComplete="off"
                          placeholder="Café / Restaurant"
                          className="mt-1 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="wholesale-contact-email" className="text-xs text-foreground">Contact email</Label>
                      <Input
                        id="wholesale-contact-email"
                        name="contact_email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="owner@thegoodcup.com"
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="wholesale-monthly-volume" className="text-xs text-foreground">Monthly volume (lbs)</Label>
                      <Input
                        id="wholesale-monthly-volume"
                        name="monthly_volume_lbs"
                        autoComplete="off"
                        inputMode="numeric"
                        placeholder="e.g. 50"
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="wholesale-message" className="text-xs text-foreground">Message</Label>
                      <Textarea
                        id="wholesale-message"
                        name="message"
                        autoComplete="off"
                        placeholder="What's your current coffee setup? What are you looking for?"
                        className="mt-1 text-sm resize-none"
                        rows={3}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
                      Submit Inquiry <ArrowRight className="w-4 h-4" />
                    </Button>
                  </form>
                  <div
                    data-hiveku-form-done
                    role="status"
                    style={{ display: "none" }}
                    className="rounded-xl border border-secondary/20 bg-secondary/5 p-4"
                  >
                    <p className="font-medium text-foreground">Thanks for reaching out.</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      We’ll be in touch within two business days.
                    </p>
                  </div>
                  <div
                    data-hiveku-form-fail
                    role="alert"
                    style={{ display: "none" }}
                    className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive"
                  >
                    We couldn’t save your inquiry. Please try again.
                  </div>
                </div>
              </MotionDiv>
            </div>
          ) : (
            /* LOGGED IN: Order portal */
            <div>
              <MotionDiv variants={fadeIn}>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-foreground">Welcome back</h2>
                    <p className="text-sm text-muted-foreground">Your wholesale ordering portal</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto text-muted-foreground"
                    onClick={() => { setIsLoggedIn(false); setEmail(""); setPassword("") }}
                  >
                    Sign out
                  </Button>
                </div>

                {/* Product catalog */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {wholesaleProducts.map((p) => (
                    <Card key={p.name} className="border-border/60 shadow-[var(--shadow-card)] bg-card">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-heading font-semibold text-foreground text-sm">{p.name}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">{p.format}</p>
                          </div>
                          <Badge variant="outline" className="text-xs border-primary/30 text-primary shrink-0">
                            {p.price}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-4">Min: {p.min}</p>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            placeholder="qty (lbs)"
                            className="flex-1 h-8 px-2.5 rounded border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                          <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs px-3">
                            Add
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Order summary */}
                <div className="bg-card border border-border/60 rounded-2xl p-6 shadow-[var(--shadow-card)] max-w-md">
                  <h3 className="font-heading font-bold text-foreground mb-4">Order Summary</h3>
                  <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border/60 rounded-xl mb-4">
                    No items added yet. Add products above.
                  </div>
                  <Button disabled className="w-full bg-accent text-accent-foreground opacity-50">
                    Place Order
                  </Button>
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    Questions? Call <a href="tel:5035550142" className="text-accent hover:underline">(503) 555-0142</a>
                  </p>
                </div>
              </MotionDiv>
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-background border-t border-border/60">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <MotionDiv variants={scaleIn}>
            <h3 className="font-heading text-2xl font-bold text-foreground mb-3">Rather talk to a human?</h3>
            <p className="text-muted-foreground text-sm mb-6">Jamie personally handles all wholesale relationships. Email or call — same response time either way.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:wholesale@example.com">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground gap-2">
                  <Mail className="w-4 h-4" /> wholesale@example.com
                </Button>
              </a>
              <a href="tel:5035550142">
                <Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground gap-2">
                  <Phone className="w-4 h-4" /> (503) 555-0142
                </Button>
              </a>
            </div>
          </MotionDiv>
        </div>
      </section>

      <Footer />
    </>
  )
}
